<template>
  <UPage class="flex-1 flex flex-col min-h-0" :ui="{ center: 'flex flex-col flex-1 min-h-0' }">
    <UPageHeader title="Vorgänge" :description="tenantName ? `Mandant: ${tenantName}` : undefined">
      <template #links>
        <UButton
          to="/microtech-hub/addresses"
          color="neutral"
          icon="i-heroicons-arrow-left"
          label="Zurück"
          variant="ghost"
        />
        <UButton
          color="neutral"
          icon="i-heroicons-cog-6-tooth"
          label="Spalten"
          variant="outline"
          @click="openColumnSelector"
        />
        <UButton
          :loading="loading"
          color="primary"
          icon="i-heroicons-arrow-path"
          label="Aktualisieren"
          @click="() => fetchTransactions(false)"
        />
      </template>
    </UPageHeader>

    <UPageBody class="p-0 sm:p-0 flex flex-col min-h-0 h-full">
      <UTabs
        v-model="currentTabIndex"
        :items="tabItems"
        :ui="{ content: 'flex-1 min-h-0 flex flex-col' }"
        class="flex flex-col h-full min-h-0"
      >
        <template #content>
          <PortalTable
            ref="portalTable"
            :all-columns="allPossibleColumns"
            :columns-loading="loading && allPossibleColumns.length === 0"
            :data="transactions"
            :loading="loading"
            :selected-columns="selectedColumns"
            :sort-storage-key="SORT_STORAGE_KEY"
            :total="totalCount"
            empty-description="Es konnten keine Vorgänge für diesen Mandanten geladen werden."
            empty-icon="i-heroicons-document-text"
            empty-title="Keine Vorgänge gefunden"
            class="h-full"
            @load-more="fetchTransactions(true)"
            @reset:columns="resetToDefault"
            @select="onRowClick"
            @update:columns="updateColumns"
          />
        </template>
      </UTabs>
    </UPageBody>
  </UPage>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import PortalTable from '@/components/PortalTable.vue'
import { useTransactionStore } from '@/stores/transaction'
import { fetchTableFields } from '@/utils/graphql-introspection'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()
const transactionStore = useTransactionStore()
const router = useRouter()

const customerNo = computed(() => authStore.userInfo?.activeTenant?.customerNo || '')
const tenantName = computed(() => authStore.userInfo?.activeTenant?.tenantName || '')

const loading = ref(false)
const transactions = ref<Record<string, unknown>[]>([])
const allPossibleColumns = ref<Array<{ key: string; label: string; graphql?: string }>>([])
const selectedColumns = ref<
  Array<{ key: string; label: string; locked?: boolean; graphql?: string }>
>([])
const portalTable = ref<InstanceType<typeof PortalTable> | null>(null)

const endCursor = ref('')
const hasNextPage = ref(false)
const limit = ref(1000)
const totalCount = ref(0)
const currentTabIndex = ref('open')

const tabItems = [
  { label: 'Offen', value: 'open' },
  { label: 'Archiv', value: 'archive' },
]

const STORAGE_KEY = computed(
  () =>
    `cc_tr_cols_${customerNo.value}_${tenantName.value}_${currentTabIndex.value === 'archive' ? 'archive' : 'open'}`,
)
const SORT_STORAGE_KEY = computed(
  () =>
    `cc_tr_sort_${customerNo.value}_${tenantName.value}_${currentTabIndex.value === 'archive' ? 'archive' : 'open'}`,
)

async function fetchMetadata() {
  if (!customerNo.value || !tenantName.value) return
  try {
    const isArchive = currentTabIndex.value === 'archive'
    const rootField = isArchive ? 'tblTransactionsArchive' : 'tblTransactions'

    const fields = await fetchTableFields(rootField, customerNo.value, tenantName.value).catch(
      () => [],
    )

    const columns: Array<{ key: string; label: string; graphql?: string }> = []
    for (const f of fields) {
      if (
        f.name.startsWith('_') ||
        f.name.endsWith('_int') ||
        f.name.startsWith('tbl') ||
        f.name.startsWith('lnk')
      )
        continue
      columns.push({
        key: f.name,
        label: f.label || f.description || f.name,
        graphql: f.graphql,
      })
    }

    allPossibleColumns.value = columns

    // Update selected columns labels from metadata
    if (selectedColumns.value.length > 0) {
      selectedColumns.value = selectedColumns.value.map((sc) => {
        const found = columns.find((c) => c.key === sc.key)
        if (found) {
          return { ...sc, label: found.label, graphql: found.graphql }
        }
        return sc
      })
    }

    loadSavedColumns()
  } catch (error) {
    console.error('Error loading metadata:', error)
  }
}

function loadSavedColumns() {
  const saved = transactionStore.loadColumns(STORAGE_KEY.value)
  if (saved) {
    const parsed = [...saved]

    // Update labels from allPossibleColumns if they match
    const updated = parsed.map((p) => {
      const found = allPossibleColumns.value.find((c) => c.key === p.key)
      if (found) {
        return { ...p, label: found.label, graphql: found.graphql }
      }
      return p
    })

    if (!updated.find((c: { key: string }) => c.key === 'fldBelegNr')) {
      updated.unshift({ key: 'fldBelegNr', label: 'Belegnummer', locked: true })
    } else {
      const idx = updated.findIndex((c: { key: string }) => c.key === 'fldBelegNr')
      updated[idx].locked = true
    }
    selectedColumns.value = updated
  } else {
    setDefaultColumns()
  }
}

function setDefaultColumns() {
  const defaults = [
    { key: 'fldAdrNr', label: 'Adressnummer' },
    { key: 'fldBelegNr', label: 'Belegnummer', locked: true },
    { key: 'fldArt', label: 'Art' },
    { key: 'fldSuchBeg', label: 'Suchbegriff' },
    { key: 'fldPrjNr', label: 'Projektnummer' },
    { key: 'fldDat', label: 'Datum' },
    { key: 'fldPrio', label: 'Priorität' },
    { key: 'fldVerk', label: 'Verkäufer' },
    { key: 'acoGPreis.totalNetAmount', label: 'Gesamtpreis: Nettobetrag' },
  ]

  // Use metadata labels if available
  const updatedDefaults = defaults.map((d) => {
    const found = allPossibleColumns.value.find((c) => c.key === d.key)
    if (found) {
      return { ...d, label: found.label, graphql: found.graphql }
    }
    return d
  })

  selectedColumns.value = updatedDefaults
  transactionStore.setColumns(STORAGE_KEY.value, updatedDefaults, false)
}

function resetToDefault() {
  transactionStore.removeColumns(STORAGE_KEY.value)
  setDefaultColumns()
  resetAndFetch()
}

function updateColumns(newCols: Array<{ key: string; label: string; locked?: boolean }>) {
  selectedColumns.value = newCols
  transactionStore.setColumns(STORAGE_KEY.value, newCols)
  resetAndFetch()
}

function openColumnSelector() {
  portalTable.value?.openColumnSelector()
}

function onRowClick(row: { original: Record<string, unknown> }) {
  const rowData = row.original
  if (!rowData) return
  const belegNr = getValue(rowData, 'fldBelegNr')
  if (belegNr) {
    const isArchive = currentTabIndex.value === 'archive'
    router.push({
      path: `/microtech-hub/transactions/${belegNr}`,
      query: { isArchive: isArchive ? 'true' : undefined },
    })
  }
}

watch(currentTabIndex, async () => {
  await fetchMetadata()
  resetAndFetch()
})

function resetAndFetch() {
  transactions.value = []
  endCursor.value = ''
  hasNextPage.value = false
  fetchTransactions()
}

async function fetchTransactions(loadMore = false) {
  if (selectedColumns.value.length === 0 || loading.value) return
  if (loadMore && (!hasNextPage.value || !endCursor.value)) return

  loading.value = true
  try {
    const selectionSet = buildSelectionSet(selectedColumns.value)
    const isArchive = currentTabIndex.value === 'archive'
    const rootField = isArchive ? 'tblTransactionsArchive' : 'tblTransactions'

    let currentAfter = loadMore && endCursor.value ? endCursor.value : ''
    let moreAvailable = true
    const allFetchedNodes: Record<string, unknown>[] = loadMore ? [...transactions.value] : []

    while (moreAvailable) {
      const afterParam = currentAfter ? `after: "${currentAfter}"` : ''
      const query = `
        query {
          ${rootField} {
            conRead(first: ${limit.value}, ${afterParam}) {
              pageInfo {
                endCursor
                hasNextPage
              }
              edges {
                node {
                  ${selectionSet}
                }
              }
            }
          }
        }
      `
      const response = await axios.post('/api/microtech/v1/proxy/graphql', { query })
      const conRead = response.data?.data?.[rootField]?.conRead

      if (!conRead) {
        console.error(`No ${rootField}.conRead data in response:`, response.data)
        moreAvailable = false
        break
      }

      const newBatch = conRead.edges.map((edge: { node: Record<string, unknown> }) => edge.node)
      allFetchedNodes.push(...newBatch)

      currentAfter = conRead.pageInfo.endCursor
      moreAvailable = conRead.pageInfo.hasNextPage

      transactions.value = [...allFetchedNodes]
      endCursor.value = currentAfter
      hasNextPage.value = moreAvailable
      totalCount.value = conRead.pageInfo.edgeCount || transactions.value.length

      if (newBatch.length === 0) moreAvailable = false
    }
  } catch (error) {
    console.error('Error loading transactions:', error)
  } finally {
    loading.value = false
  }
}

function buildSelectionSet(columns: Array<{ key: string; graphql?: string }>) {
  const customSelections = new Set<string>()
  const tree: Record<string, unknown> = {}

  columns.forEach((col) => {
    if (col.graphql && col.graphql.includes('{')) {
      customSelections.add(col.graphql)
    } else {
      const parts = (col.graphql || col.key).split('.')
      let current = tree as Record<string, unknown>
      parts.forEach((part) => {
        if (!current[part]) {
          current[part] = {} as Record<string, unknown>
        }
        current = current[part] as Record<string, unknown>
      })
    }
  })

  function stringify(obj: Record<string, unknown>): string {
    return Object.keys(obj)
      .map((k) => {
        const val = obj[k]
        if (val && typeof val === 'object' && Object.keys(val).length > 0) {
          const sub = stringify(val as Record<string, unknown>)
          return `${k} { ${sub} }`
        }
        return k
      })
      .join('\n')
  }

  const baseSelection = stringify(tree)
  return [baseSelection, ...Array.from(customSelections)].filter((s) => s).join('\n')
}

function getValue(obj: Record<string, unknown>, key: string) {
  return key.split('.').reduce((o: Record<string, unknown> | undefined, i) => {
    if (o && typeof o === 'object' && i in o) {
      return o[i] as Record<string, unknown> | undefined
    }
    return undefined
  }, obj)
}

onMounted(async () => {
  if (customerNo.value && tenantName.value) {
    await fetchMetadata()
    fetchTransactions()
  }

  watch([customerNo, tenantName], async ([newCust, newTen]) => {
    if (newCust && newTen) {
      await fetchMetadata()
      fetchTransactions()
    }
  })
})
</script>
